import { useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import client from "@/lib/api";
import { ErrorState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { ArrowLeft, Loader2, Trash2, ImagePlus, Save, SplitSquareHorizontal, FileDown } from "lucide-react";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";
import { Lightbox } from "@/components/Lightbox";
import { ShareRow, mediaThumb, mediaFull, downloadPdf } from "@/pages/Portfolio";

function BeforeAfterDialog({ projectId, onSaved }) {
  const [open, setOpen] = useState(false);
  const [before, setBefore] = useState(null);
  const [after, setAfter] = useState(null);
  const [caption, setCaption] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!before || !after) { toast.error("Add both a before and an after photo"); return; }
    setSaving(true);
    try {
      const fd = new FormData(); fd.append("before", before); fd.append("after", after); fd.append("caption", caption);
      await client.post(`/portfolio/projects/${projectId}/before-after`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Before / after added"); setBefore(null); setAfter(null); setCaption(""); setOpen(false); onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add pair"); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" className="gap-2" data-testid="add-before-after-btn"><SplitSquareHorizontal className="h-4 w-4" />Add before / after</Button></DialogTrigger>
      <DialogContent data-testid="before-after-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">Before &amp; after</DialogTitle><DialogDescription>Upload the two photos — customers drag to reveal the transformation.</DialogDescription></DialogHeader>
        <div className="grid gap-4 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5"><Label>Before</Label><Input type="file" accept="image/*" onChange={(e) => setBefore(e.target.files?.[0] || null)} data-testid="ba-before-input" /></div>
            <div className="grid gap-1.5"><Label>After</Label><Input type="file" accept="image/*" onChange={(e) => setAfter(e.target.files?.[0] || null)} data-testid="ba-after-input" /></div>
          </div>
          <div className="grid gap-1.5"><Label>Caption (optional)</Label><Input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="e.g. Full ensuite retile" data-testid="ba-caption-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="ba-save-btn">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Add pair</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PortfolioProject() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const photoRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [lb, setLb] = useState(null);
  const [details, setDetails] = useState(null);

  const { data: p, isLoading, isError, refetch } = useQuery({
    queryKey: ["portfolio-project", projectId],
    queryFn: async () => { const d = (await client.get(`/portfolio/projects/${projectId}`)).data; setDetails({ title: d.title, location: d.location, description: d.description }); return d; },
  });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["portfolio-project", projectId] }); };

  const saveDetails = async () => {
    if (!details.title.trim()) { toast.error("Title is required"); return; }
    setSavingDetails(true);
    try { await client.put(`/portfolio/projects/${projectId}`, details); toast.success("Saved"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); } finally { setSavingDetails(false); }
  };
  const uploadPhotos = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const f of files) { const fd = new FormData(); fd.append("file", f); await client.post(`/portfolio/projects/${projectId}/images`, fd, { headers: { "Content-Type": "multipart/form-data" } }); }
      toast.success(`${files.length} photo(s) added`); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't upload"); } finally { setUploading(false); }
  };
  const delPhoto = async (mediaId) => { try { await client.delete(`/portfolio/projects/${projectId}/images/${mediaId}`); refresh(); } catch { toast.error("Couldn't remove photo"); } };
  const delPair = async (baId) => { try { await client.delete(`/portfolio/projects/${projectId}/before-after/${baId}`); toast.success("Removed"); refresh(); } catch { toast.error("Couldn't remove"); } };
  const delProject = async () => {
    if (!window.confirm(`Delete project "${p?.title}"? This can't be undone.`)) return;
    try { await client.delete(`/portfolio/projects/${projectId}`); toast.success("Project deleted"); navigate(`/portfolio/${p.category_id}`); }
    catch { toast.error("Couldn't delete project"); }
  };

  if (isLoading || !details) return <div className="h-64 animate-pulse rounded-2xl bg-muted/50" />;
  if (isError || !p) return <ErrorState onRetry={refetch} message="We couldn't load this project." />;

  return (
    <div className="mx-auto max-w-4xl animate-fade-up" data-testid="portfolio-project-page">
      <button onClick={() => navigate(`/portfolio/${p.category_id}`)} data-testid="back-to-category" className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-ink"><ArrowLeft className="h-4 w-4" />{p.category?.name || "Category"}</button>
        <Button variant="outline" size="sm" onClick={() => downloadPdf(`/portfolio/projects/${projectId}/pdf`, `TilesMate-${p.title}.pdf`, setPdfBusy)} disabled={pdfBusy} data-testid="project-pdf-btn" className="mb-4 ml-3 gap-1.5">{pdfBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />}Download PDF</Button>

      <div className="mb-6 grid gap-4 lg:grid-cols-[1fr_320px]">
        <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-[0_2px_12px_rgba(35,52,90,0.04)]">
          <div className="grid gap-3">
            <div className="grid gap-1.5"><Label className="text-xs">Title</Label><Input value={details.title} onChange={(e) => setDetails((d) => ({ ...d, title: e.target.value }))} data-testid="edit-project-title" /></div>
            <div className="grid gap-1.5"><Label className="text-xs">Location / suburb</Label><Input value={details.location} onChange={(e) => setDetails((d) => ({ ...d, location: e.target.value }))} data-testid="edit-project-location" /></div>
            <div className="grid gap-1.5"><Label className="text-xs">Description</Label><Textarea value={details.description} onChange={(e) => setDetails((d) => ({ ...d, description: e.target.value }))} data-testid="edit-project-desc" /></div>
            <div className="flex items-center justify-between">
              <Button variant="outline" size="sm" onClick={delProject} data-testid="delete-project-btn" className="gap-1.5 text-error hover:text-error"><Trash2 className="h-3.5 w-3.5" />Delete</Button>
              <Button size="sm" onClick={saveDetails} disabled={savingDetails} data-testid="save-project-btn" className="gap-1.5">{savingDetails ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}Save details</Button>
            </div>
          </div>
        </div>
        <ShareRow kind="project" id={projectId} enabled={p.share_enabled} token={p.share_token} onChanged={refresh} />
      </div>

      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-display text-base font-bold text-ink">Photos <span className="text-xs font-normal text-muted-foreground">· {p.images.length}</span></h2>
        <input ref={photoRef} type="file" accept="image/*" multiple className="hidden" data-testid="project-photos-input" onChange={(e) => { uploadPhotos(Array.from(e.target.files || [])); e.target.value = ""; }} />
        <Button variant="outline" size="sm" onClick={() => photoRef.current?.click()} disabled={uploading} className="gap-1.5" data-testid="add-photos-btn">{uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}Add photos</Button>
      </div>
      {p.images.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-border bg-white/60 p-8 text-center text-sm text-muted-foreground">No photos yet. Add finished-work shots for this project.</p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4" data-testid="project-photo-grid">
          {p.images.map((im) => (
            <div key={im.media_id} className="group relative aspect-square overflow-hidden rounded-xl border border-slate-200 bg-slate-50" data-testid={`project-photo-${im.media_id}`}>
              <img src={mediaThumb(im.media_id)} alt={im.caption || ""} draggable={false} onClick={() => setLb({ images: p.images.map((x) => ({ url: mediaFull(x.media_id), caption: x.caption })), index: p.images.findIndex((x) => x.media_id === im.media_id) })} className="h-full w-full cursor-zoom-in object-cover" />
              <button onClick={() => delPhoto(im.media_id)} data-testid={`del-photo-${im.media_id}`} className="absolute right-1.5 top-1.5 grid h-7 w-7 place-items-center rounded-full bg-black/55 text-white opacity-0 transition-opacity hover:bg-error group-hover:opacity-100"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      )}

      <div className="mb-3 mt-8 flex items-center justify-between">
        <h2 className="font-display text-base font-bold text-ink">Before &amp; after <span className="text-xs font-normal text-muted-foreground">· {p.before_after.length}</span></h2>
        <BeforeAfterDialog projectId={projectId} onSaved={refresh} />
      </div>
      {p.before_after.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-border bg-white/60 p-8 text-center text-sm text-muted-foreground">No before/after pairs yet. Add a pair to show off a transformation.</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="project-ba-grid">
          {p.before_after.map((ba) => (
            <div key={ba.id} className="rounded-2xl border border-slate-100 bg-white p-3 shadow-[0_2px_12px_rgba(35,52,90,0.04)]" data-testid={`project-ba-${ba.id}`}>
              <BeforeAfterSlider beforeUrl={mediaFull(ba.before_media_id)} afterUrl={mediaFull(ba.after_media_id)} />
              <div className="mt-2 flex items-center justify-between gap-2">
                <span className="truncate text-sm text-muted-foreground">{ba.caption || "Before / after"}</span>
                <button onClick={() => delPair(ba.id)} data-testid={`del-ba-${ba.id}`} className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-red-50 hover:text-error"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {lb && <Lightbox images={lb.images} startIndex={lb.index} onClose={() => setLb(null)} />}
    </div>
  );
}
